import type { JobData } from '@/domains/jobs/hooks/useJobData';
import {
  useExtendPermit,
  useStartPermit,
  useStopPermit,
} from '@/domains/jobs/hooks/usePermitActions';
import { formatPermitDateTime } from '@/shared/lib/date-utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  PERMIT_CATEGORY,
  PERMIT_STATUS,
  isPermitOrderType,
  permitProgressionUngated,
} from '@skewb-works/contracts';
import type {
  ActionablePermitCategory,
  JobPermit,
  PermitCategory,
  PermitLifecycleStatus,
} from '@skewb-works/contracts';
import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

/**
 * The permit workflow only applies to ZRP1 / ZRC2 order types (the card is
 * hidden otherwise). Among those, the permit category CORE resolves after the
 * SAP DISP confirmation drives the actions:
 *
 *  - planned   → user Starts the permit once On Site, then Extend / Stop while active.
 *  - immediate → permit arrives already started; Extend / Stop once On Site.
 *  - none      → no permit workflow; the card is hidden.
 *  - null / pending → category not received yet; the card shows a waiting hint.
 *
 * CORE arranges every permit itself off the SAP DISP confirmation, so there is no
 * Request Permit action in this flow (CWL-125). A ZRP1 + immediate job with no
 * permit row yet hides the card entirely rather than showing a waiting hint — the
 * work order is not gated on that permit, so a "waiting" message misrepresented
 * it as blocked.
 *
 * Every lifecycle action (Start / Extend / Stop) is only enabled once the
 * engineer is On Site — before that the buttons render disabled with a hint.
 * The backend mirrors this in permitGate.ts (assertPermitActionAllowed).
 */

// The permit actions (Start / Extend / Stop) unlock only once the engineer is
// On Site — before that they render disabled with this hint.
const ON_SITE_HINT = 'Available once the engineer is on site.';

function PermitCardShell({ subtitle, children }: { subtitle: string; children: ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Permit</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ExtendPermitButton({
  jobId,
  permit,
  disabled = false,
}: {
  jobId: number;
  permit: JobPermit;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const extendMutation = useExtendPermit(jobId, permit.id);

  const submit = () => {
    // datetime-local yields a zoneless wall-clock string; the contract wants an
    // ISO instant (extendDate: z.string().datetime()).
    extendMutation.mutate(new Date(extendDate).toISOString(), {
      onSuccess: () => setOpen(false),
    });
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? ON_SITE_HINT : undefined}
      >
        Extend
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setExtendDate('');
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Extend Permit</DialogTitle>
            <DialogDescription>
              Choose the new permit end date. This is sent to SAP as an extension request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Label htmlFor="permit-extend-date">New permit end date</Label>
            <Input
              id="permit-extend-date"
              type="datetime-local"
              value={extendDate}
              onChange={(e) => setExtendDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={extendMutation.isPending}
            >
              Cancel
            </Button>
            <Button disabled={!extendDate || extendMutation.isPending} onClick={submit}>
              {extendMutation.isPending ? 'Requesting…' : 'Request Extension'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PermitActionButtons({
  jobId,
  category,
  permit,
  isOnSite,
}: {
  jobId: number;
  category: ActionablePermitCategory;
  permit: JobPermit;
  isOnSite: boolean;
}) {
  const startMutation = useStartPermit(jobId, permit.id);
  const stopMutation = useStopPermit(jobId, permit.id);
  const status: PermitLifecycleStatus = permit.status;

  // Only a planned permit sits in 'arranged' awaiting a Start; immediate arrives active.
  if (status === PERMIT_STATUS.ARRANGED) {
    if (category === PERMIT_CATEGORY.IMMEDIATE) return null;
    return (
      <Button
        onClick={() => startMutation.mutate()}
        disabled={!isOnSite || startMutation.isPending}
        title={isOnSite ? undefined : ON_SITE_HINT}
      >
        {startMutation.isPending ? 'Starting…' : 'Start'}
      </Button>
    );
  }

  if (status === PERMIT_STATUS.PENDING_START) {
    return (
      <Button disabled variant="outline">
        Pending start
      </Button>
    );
  }

  if (status === PERMIT_STATUS.ACTIVE) {
    return (
      <div className="flex gap-2">
        <ExtendPermitButton jobId={jobId} permit={permit} disabled={!isOnSite} />
        <Button
          onClick={() => stopMutation.mutate()}
          disabled={!isOnSite || stopMutation.isPending}
          title={isOnSite ? undefined : ON_SITE_HINT}
        >
          {stopMutation.isPending ? 'Stopping…' : 'Stop'}
        </Button>
      </div>
    );
  }

  // Extend request sent to SAP — hold Extend/Stop and surface a waiting note
  // until the extension confirmation lands and flips the permit back to active.
  if (status === PERMIT_STATUS.PENDING_EXTENSION) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button variant="outline" disabled>
            Extend
          </Button>
          <Button disabled>Stop</Button>
        </div>
        <p className="text-sm text-muted-foreground">Waiting for extension approval…</p>
      </div>
    );
  }

  if (status === PERMIT_STATUS.PENDING_STOP) {
    return (
      <Button disabled variant="outline">
        Pending stop
      </Button>
    );
  }

  return null;
}

function PermitDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}

export function PermitCard({ jobData }: { jobData: JobData }) {
  // Only ZRP1 / ZRC2 order types carry a permit workflow. Every other order
  // type (ZOS1, ZOS2, ZRP2, ZBF1, …) has no permit — hide the card entirely.
  if (!isPermitOrderType(jobData.orderType)) return null;

  const category: PermitCategory | null = jobData.permitCategory ?? null;

  if (category === PERMIT_CATEGORY.NONE) return null;

  // Category not received yet — the En Route gate holds the WO at Scheduled meanwhile.
  if (category == null || category === PERMIT_CATEGORY.PENDING) {
    return (
      <PermitCardShell subtitle="Awaiting permit category">
        <div className="text-sm text-muted-foreground">
          Waiting for the permit category from CORE…
        </div>
      </PermitCardShell>
    );
  }

  const permit = jobData.permit ?? null;

  if (!permit) {
    // CWL-125: a ZRP1 immediate permit may arrive late or never, and the work
    // order is not gated on it (permitProgressionUngated), so there is nothing
    // for the engineer to wait for — showing "Waiting for permit details…"
    // implied the job was blocked when it wasn't. Hide the card until a real
    // permit lands, exactly as a resolved `none` does; the card reappears with
    // its actions the moment CORE sends one.
    if (permitProgressionUngated(jobData.orderType, category)) return null;

    // A planned permit is always arranged by CORE, so it only ever waits. There
    // is no Request Permit action in the category-driven flow — CORE arranges
    // every permit off the back of the SAP DISP confirmation.
    return (
      <PermitCardShell
        subtitle={category === PERMIT_CATEGORY.IMMEDIATE ? 'Immediate permit' : 'Planned permit'}
      >
        <div className="text-sm text-muted-foreground">
          {category === PERMIT_CATEGORY.IMMEDIATE
            ? 'Waiting for permit details from SAP…'
            : 'Waiting for the permit to be arranged…'}
        </div>
      </PermitCardShell>
    );
  }

  // Permit actions are only available once the engineer is On Site (per the
  // flow doc). Resolved server-side so this matches the API's own gate — it
  // stays true after the work order auto-promotes past On Site, which is when
  // the engineer typically stops the permit (CWL-118).
  const isOnSite = jobData.engineerOnSite === true;

  return (
    <PermitCardShell
      subtitle={category === PERMIT_CATEGORY.IMMEDIATE ? 'Immediate permit' : 'Planned permit'}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <PermitDetailRow label="Permit Number" value={permit.permit_reference} />
          <PermitDetailRow label="Permit Start" value={formatPermitDateTime(permit.start_date)} />
          <PermitDetailRow label="Permit End" value={formatPermitDateTime(permit.end_date)} />
        </div>
        <PermitActionButtons
          jobId={Number(jobData.id)}
          category={category}
          permit={permit}
          isOnSite={isOnSite}
        />
      </div>
    </PermitCardShell>
  );
}
